/* eslint-disable @atlassian/relay/must-use-inline, @atlassian/relay/unused-fields -- Shared resource operations and canonical snapshots are consumed by the resource owner, readers, and store reconciliation. */
import { fetchQuery, graphql } from 'react-relay';
import type { GraphQLTaggedNode } from 'relay-runtime';
import {
	createOperationDescriptor,
	createReaderSelector,
	getFragment,
	getRequest,
	type Disposable,
	type IEnvironment,
	type Snapshot,
} from 'relay-runtime';

import RelayDataID from '@atlassian/relay-data-id';

import type { agentSessionHydrationQuery } from './__generated__/agentSessionHydrationQuery.graphql';
import type { agentSessionSnapshot_session$data } from './__generated__/agentSessionSnapshot_session.graphql';
import { agentSessionSnapshot } from './agentSessionSnapshot';
import { escapeAgentSessionAqlLiteral } from './agentSessionsPolicy';
import type { ResolvedAgentSessionsScope } from './AgentSessionsScopeProvider';
import { getAgentSessionsScopeKey } from './AgentSessionsScopeProvider';

export const agentSessionHydration: GraphQLTaggedNode = graphql`
	query agentSessionHydrationQuery($sharingContext: String!, $aqlQuery: String!) {
		agent_sessionQuery(
			shardingContext: $sharingContext
			aqlQuery: $aqlQuery
			sort: "lastStateChangeAt DESC"
		) {
			nodes {
				hasUnseenActivity
				invocationType
				...agentSessionSnapshot_session @relay(mask: false)
			}
		}
	}
`;

const variablesFor = (scope: ResolvedAgentSessionsScope, sessionAri: string) => ({
	sharingContext: scope.workspaceAri,
	aqlQuery: `authorAccountId = '${escapeAgentSessionAqlLiteral(scope.accountId)}' AND sessionAri = '${escapeAgentSessionAqlLiteral(sessionAri)}'`,
});

export const readAgentSessionDisplay = (
	environment: IEnvironment,
	scope: ResolvedAgentSessionsScope,
	sessionId: string,
	sessionAri: string,
): Snapshot & { data: agentSessionSnapshot_session$data } => {
	const operation = createOperationDescriptor(
		getRequest(agentSessionHydration),
		variablesFor(scope, sessionAri),
	);
	const selector = createReaderSelector(
		getFragment(agentSessionSnapshot),
		RelayDataID({ id: sessionId }, 'AgentSession')!,
		operation.request.variables,
		operation.request,
	);
	return environment.lookup(selector) as Snapshot & { data: agentSessionSnapshot_session$data };
};

type HydrationResult = Readonly<{ found: boolean; error: Error | null }>;
type Job = { listeners: Set<(result: HydrationResult) => void>; dispose: () => void };
const requests = new WeakMap<IEnvironment, Map<string, Job>>();

export const hydrateAgentSession = (
	environment: IEnvironment,
	scope: ResolvedAgentSessionsScope,
	sessionId: string,
	sessionAri: string,
	onComplete: (result: HydrationResult) => void,
): Disposable => {
	let pending = requests.get(environment);
	if (!pending) {
		pending = new Map();
		requests.set(environment, pending);
	}
	const key = JSON.stringify([getAgentSessionsScopeKey(scope), sessionId, sessionAri]);
	let job = pending.get(key);
	if (!job) {
		const variables = variablesFor(scope, sessionAri);
		const operation = createOperationDescriptor(getRequest(agentSessionHydration), variables);
		const retention = environment.retain(operation);
		let network: { unsubscribe: () => void } | undefined;
		let finished = false;
		let found = false;
		const listeners = new Set([onComplete]);
		job = {
			listeners,
			dispose: () => {
				network?.unsubscribe();
				retention.dispose();
			},
		};
		const current = job;
		pending.set(key, current);
		const finish = (error: Error | null) => {
			if (finished) return;
			finished = true;
			pending?.delete(key);
			try {
				current.listeners.forEach((listener) => listener({ found, error }));
			} finally {
				current.dispose();
			}
		};
		network = fetchQuery<agentSessionHydrationQuery>(
			environment,
			agentSessionHydration,
			variables,
			{ fetchPolicy: 'network-only' },
		).subscribe({
			next: (data) => {
				found = data.agent_sessionQuery?.nodes?.some((node) => node?.id === sessionId) ?? false;
			},
			error: (error: Error) => finish(error),
			complete: () => finish(null),
		});
	} else job.listeners.add(onComplete);
	const current = job;
	return {
		dispose: () => {
			current.listeners.delete(onComplete);
			if (!current.listeners.size && pending?.get(key) === current) {
				pending.delete(key);
				current.dispose();
			}
		},
	};
};
